import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import messages from './local/index';
import { addressLanguage } from '../router/languageAddress';
import { isCrawler } from '../seo/site';

export const SUPPORTED_LANGUAGES = ['pt', 'en', 'es', 'fr'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Reduces any language tag ('en-US', 'pt_BR', 'FR') to one of the supported base codes. */
export function toSupportedLanguage(lng: string | undefined | null): SupportedLanguage {
  const base = (lng || '').toLowerCase().split(/[-_]/)[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(base) ? (base as SupportedLanguage) : 'pt';
}

function syncHtmlLang(lng: string | undefined) {
  if (typeof document === 'undefined') return;
  const code = toSupportedLanguage(lng);
  document.documentElement.lang = code === 'pt' ? 'pt-BR' : code;
}

// Registered before init so the initial (detected) language also updates <html lang>.
i18n.on('languageChanged', syncHtmlLang);

const detector = new LanguageDetector();
// The address says the language first (/es/escunas is Spanish). A plain address is Portuguese for search
// engines and link previews, whatever their browser language; visitors get their saved or browser language
// (router/LangRouter.tsx then moves them to that language's address).
detector.addDetector({
  name: 'address',
  lookup() {
    if (typeof window === 'undefined') return undefined;
    return addressLanguage(window.location.pathname) ?? (isCrawler(navigator.userAgent) ? 'pt' : undefined);
  },
});

i18n
  .use(detector)
  .use(initReactI18next)
  .init(
    {
      resources: messages,
      supportedLngs: [...SUPPORTED_LANGUAGES],
      // 'en-US' is not listed, so i18next falls back to its base code 'en'.
      nonExplicitSupportedLngs: false,
      load: 'languageOnly',
      fallbackLng: 'pt',
      debug: false,
      detection: {
        order: ['address', 'localStorage', 'navigator'],
        caches: ['localStorage'],
        lookupLocalStorage: 'i18nextLng',
        // Keep only the base code so an unsupported first browser language ('de-DE')
        // does not hide a supported one later in navigator.languages ('en-US').
        convertDetectedLanguage: (lng: string) => (lng || '').toLowerCase().split(/[-_]/)[0],
      },
      interpolation: {
        escapeValue: false,
      },
    },
    () => syncHtmlLang(i18n.resolvedLanguage || i18n.language),
  );

export default i18n;
